import type { AuthorizationPolicy } from "@tdelivr/api-client/generated/identity/authorization-policy.js";
import type { StateClassKey } from "@tdelivr/design-tokens";

import { asDay, type BoardRow } from "./board.ts";

/**
 * What the roles board does to the policies IAM-03 returns, away from any rendering.
 *
 * **This page's recorded blocker was true and is now closed, and it was the whole blocker.** It
 * read: "IAM-03 publishes two policy writes and no policy read. A roles page cannot show a role that
 * has already been created, so it would be a form with no list behind it." Measured against the
 * service rather than the note: the repository had `listForTenant` and `findById` the whole time,
 * because `decide` evaluates every policy a tenant holds — what was missing was only the HTTP
 * surface. ADR 0080 publishes it.
 *
 * Three absences remain, and none of them is the listing. They are written here because this is the
 * file that decides what each column says.
 *
 * **`Members` has no source anywhere, and it is not a near miss.** The design's grid is
 * `Role · Members · Sensitive`, with `2`, `3`, `28`, `11` down the middle. IAM-03 holds no people
 * at all: a policy has `rules`, and a rule has `subjectRoles`, which is the list of role *names* the
 * rule matches — not a list of the people in them, and not a count of anything. Rendering
 * `subjectRoles.length` as `Members` would publish "role names this policy mentions" under the word
 * "Members", which is the invention this console exists not to make. The column carries the absence,
 * which is the same decision the customers board took for `Phone` (Q-103) and for the same reason: a
 * number here would read as a number this console holds.
 *
 * **`Sensitive` has a real field near it and the design's words are not that field.** Its cells read
 * `all`, `payout:approve`, `—` and `refund&nbsp;issue ≤ KES 2,000`, and its badges read `protected`,
 * `two-person`, `standard` and `limited`. A rule's `requiredScopes` is a published list of the
 * scopes a caller must hold, which is genuinely "the permissions this role carries" — so it is
 * drawn, in the detail panel, labelled for what it is. What has no source is the **classification**:
 * nothing on this platform marks a scope as sensitive, nothing records that a permission needs a
 * second person, and `≤ KES 2,000` is a limit no field holds. So the column shows the scopes and
 * says it is showing scopes, and the words the design puts in the badge are not reproduced.
 *
 * **None of the four tiles has a source**, which is this console's established answer rather than a
 * new one — the brand, catalogue, customers and configuration boards each record the same, and
 * `RecordsBoard` has no tiles for that reason. `Roles · 9` and `Custom · 4` are totals over a
 * bounded read, which R-04 measured that no listing on this platform publishes; `Custom` also needs
 * a built-in/custom distinction nothing draws, because every policy in IAM-03 was created by a
 * tenant. `Sensitive permissions · 6` is the classification above. `Unused roles · 1 · 30 days`
 * needs a last-used instant, and IAM-03 records `updatedAt` — when the policy was last *changed*,
 * which is a different fact and would read as "unused" for a role in constant use that nobody has
 * edited.
 */

/**
 * The state class and the word, from the one thing a policy's own rules decide.
 *
 * A policy has no status field — there is nothing to be active or suspended — so this badge is not
 * the design's sensitivity class, which has no source. It is the composition of the rules' `effect`,
 * which is published, and it is the distinction an administrator reading this board actually needs:
 * a policy containing a `deny` takes something away from whoever it matches, and reads differently
 * from one that only grants.
 *
 *   - any `deny` rule is `caution` — the class whose label is "Qualified": in use, with a condition.
 *   - all `allow` is `done`.
 *
 * A policy with no rules cannot exist: the create and replace commands both require at least one.
 */
export function stateOf(policy: AuthorizationPolicy): {
  classKey: StateClassKey;
  label: string;
  title?: string;
} {
  const denies = policy.rules.filter((rule) => rule.effect === "deny").length;
  return denies > 0
    ? {
        classKey: "caution",
        label: denies === 1 ? "1 deny rule" : `${String(denies)} deny rules`,
        title:
          "This policy denies as well as allows, so it takes permission away from whoever its rules match. IAM-03 publishes no sensitivity classification, so this badge is the rules' own effect rather than the design's word.",
      }
    : {
        classKey: "done",
        label: "allow only",
        title:
          "Every rule in this policy grants. IAM-03 publishes no sensitivity classification, so this badge is the rules' own effect rather than the design's word.",
      };
}

/**
 * Every scope the policy's rules require, deduplicated and in the service's own order.
 *
 * Across rules rather than per rule, because the board shows one line for a role and a role is the
 * policy. Deduplicated because two rules commonly require the same scope, and a list repeating it
 * would read as two separate permissions.
 */
export function scopesOf(policy: AuthorizationPolicy): readonly string[] {
  return [...new Set(policy.rules.flatMap((rule) => rule.requiredScopes))];
}

/** The actions the policy's rules name, deduplicated, which is what the role can be used to do. */
export function actionsOf(policy: AuthorizationPolicy): readonly string[] {
  return [...new Set(policy.rules.flatMap((rule) => rule.actions))];
}

/** One row of the roles board. */
export function policyRow(policy: AuthorizationPolicy): BoardRow {
  const state = stateOf(policy);
  const scopes = scopesOf(policy);
  return {
    id: policy.id,
    cells: [
      { text: policy.name },
      {
        // The design's `Members`. IAM-03 holds no people; see this file's head.
        text: "",
        absent: true,
        title:
          "IAM-03 holds policies, not people. Nothing on this platform publishes who is in a role, so a number here would be invented. A rule's subjectRoles names role names, not members.",
      },
      scopes.length === 0
        ? {
            // A real emptiness rather than a missing field: the rules require no scope. The design
            // writes an em dash in this cell for the same case, so the dash is its own word.
            text: "—",
            title:
              "None of this policy's rules requires a scope. The rules still match on action, resource type and subject role.",
          }
        : {
            text: scopes.length === 1 ? scopes[0]! : `${scopes[0]!} +${String(scopes.length - 1)}`,
            mono: true,
            title: `Scopes this role's rules require: ${scopes.join(", ")}. IAM-03 publishes no sensitivity marking, so this column shows the scopes rather than the design's classification.`,
          },
    ],
    state,
    detail: [
      { label: "Policy", value: policy.id, mono: true },
      ...(policy.description === undefined
        ? []
        : [{ label: "Description", value: policy.description }]),
      {
        label: policy.rules.length === 1 ? "Rule" : "Rules",
        value: policy.rules
          .map((rule) => `${rule.id} · ${rule.effect} · ${rule.resourceTypes.join(", ")}`)
          .join(" | "),
        mono: true,
      },
      { label: "Actions", value: actionsOf(policy).join(", ") || "—", mono: true },
      { label: "Required scopes", value: scopes.join(", ") || "—", mono: true },
    ],
    history: [
      { label: "Created", value: asDay(policy.createdAt) },
      { label: "Last changed", value: asDay(policy.updatedAt) },
      { label: "Version", value: `v${String(policy.version)}` },
    ],
    glyph: "shield_person",
  };
}

/**
 * The filter's options, and why they are the rules' effect rather than a status.
 *
 * `GET /v1/tenants/{tenantId}/policies` takes `limit` and `after` and no filter at all, so these
 * narrow the rows already fetched — unlike the customers board, whose filter refetches because its
 * operation accepts a `status`. The vocabulary is the one thing a policy's own rules decide, which
 * is what the badge beside it shows; offering the design's `protected`, `two-person`, `standard` and
 * `limited` would be a filter over a classification nothing publishes, and every option but one
 * would empty the board.
 */
export const POLICY_EFFECTS: readonly { readonly value: string; readonly label: string }[] = [
  { value: "allow", label: "allow only" },
  { value: "deny", label: "has a deny rule" },
];

/** Whether a policy belongs under the chosen effect filter. `""` is unfiltered. */
export function matchesEffect(policy: AuthorizationPolicy, effect: string): boolean {
  if (effect === "") return true;
  const denies = policy.rules.some((rule) => rule.effect === "deny");
  return effect === "deny" ? denies : !denies;
}
